import React, { Component, Suspense } from 'react'
import { Box, FormControl, TextField } from '@mui/material'
import { ToggleButtons } from '@/shared/ui/buttons'
import { FormInputLabel } from '@/shared/ui/input'

// KedoMonacoEditor.js has no type declarations; cast to silence TS7016.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const KedoMonacoEditor = React.lazy(() => import('./KedoMonacoEditor' as any))

export type EditorMode = 'code' | 'plain'

export type DualModeEditorProps = {
    value: string
    onChange: (value: string) => void
    kedo: unknown
    language?: string
    originEntity?: unknown
    multiline?: boolean
    rows?: number
    label?: string
    togglePlacement?: 'top' | 'bottom'
}

type State = {
    editorMode: EditorMode
}

class DualModeEditor extends Component<DualModeEditorProps, State> {
    private editorId: string

    constructor(props: DualModeEditorProps) {
        super(props)
        this.state = { editorMode: 'code' }
        // Stable ID for Monaco language scoping; generated once per instance.
        this.editorId = `editor-${Math.random().toString(36).slice(2)}`
    }

    render() {
        const {
            value,
            onChange,
            kedo,
            language = 'kedoexpr',
            originEntity,
            multiline = true,
            rows = 3,
            label,
            togglePlacement = 'bottom',
        } = this.props
        const { editorMode } = this.state
        const kedoInst = kedo as { t: (k: string) => string }
        const t = kedoInst.t.bind(kedoInst)

        const toggle = (
            <ToggleButtons
                value={editorMode}
                onChange={(val) =>
                    this.setState({ editorMode: val as EditorMode })
                }
                options={[
                    { value: 'code', label: t('Editor') },
                    { value: 'plain', label: t('Text') },
                ]}
            />
        )

        const editor = (
            <FormControl fullWidth>
                {editorMode === 'code' ? (
                    <Suspense fallback={<div>Loading editor...</div>}>
                        <KedoMonacoEditor
                            kedo={kedo}
                            language={language}
                            originEntity={originEntity}
                            editorId={this.editorId}
                            value={value}
                            onChange={onChange}
                        />
                    </Suspense>
                ) : (
                    <TextField
                        multiline={multiline}
                        rows={rows}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        fullWidth
                        size="small"
                    />
                )}
            </FormControl>
        )

        return (
            <Box>
                {label ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.5,
                        }}
                    >
                        <FormInputLabel>{label}</FormInputLabel>
                        {togglePlacement === 'top' && (
                            <Box sx={{ mb: 1 }}>{toggle}</Box>
                        )}
                    </Box>
                ) : (
                    togglePlacement === 'top' && (
                        <Box sx={{ mb: 1 }}>{toggle}</Box>
                    )
                )}
                {editor}
                {togglePlacement === 'bottom' && (
                    <Box sx={{ mt: 1 }}>{toggle}</Box>
                )}
            </Box>
        )
    }
}

export default DualModeEditor
